import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { uploadToCloudinary } from "@/lib/uploads";
import { toast } from "sonner";
import { Pin, EyeOff, Globe, FileText, Plus, Trash2, ArrowUp, ArrowDown, Copy, ExternalLink, Image as ImageIcon, X } from "lucide-react";
import AdminGuideManager from "@/components/admin/AdminGuideManager";

const ICON_CHOICES = [
  { v: "notebook", label: "Notebook" }, { v: "bell", label: "Bell" },
  { v: "camera", label: "Camera" }, { v: "hexagon", label: "Hexagon" },
];

export default function AdminCmsPanel() {
  return (
    <div className="bg-white border border-stone-200 rounded-2xl p-6" data-testid="admin-cms-panel">
      <h2 className="font-display text-xl font-medium mb-1">Content management</h2>
      <p className="text-xs text-stone-500 mb-4">Featured apiaries, landing-page copy, and custom pages.</p>
      <Tabs defaultValue="featured">
        <TabsList>
          <TabsTrigger value="featured" data-testid="cms-tab-featured">Hive of the Week</TabsTrigger>
          <TabsTrigger value="landing" data-testid="cms-tab-landing">Landing page</TabsTrigger>
          <TabsTrigger value="pages" data-testid="cms-tab-pages">Pages</TabsTrigger>
          <TabsTrigger value="guide" data-testid="cms-tab-guide">Guide</TabsTrigger>
        </TabsList>
        <TabsContent value="featured"><FeaturedManager /></TabsContent>
        <TabsContent value="landing"><LandingEditor /></TabsContent>
        <TabsContent value="pages"><PagesManager /></TabsContent>
        <TabsContent value="guide"><AdminGuideManager /></TabsContent>
      </Tabs>
    </div>
  );
}

// ───── Featured override ─────
function FeaturedManager() {
  const [items, setItems] = useState(null);

  const load = async () => {
    const { data } = await api.get("/admin/featured/eligible");
    setItems(data);
  };
  useEffect(() => { load(); }, []);

  const setOverride = async (owner_id, kind) => {
    try {
      await api.post("/admin/featured/overrides", { owner_id, kind });
      toast.success(`${kind === "pin" ? "Pinned" : "Hidden"}`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const clearOverride = async (owner_id) => {
    try {
      await api.delete(`/admin/featured/overrides/${owner_id}`);
      toast.success("Cleared");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  if (!items) return <p className="text-sm text-stone-500 mt-4">Loading…</p>;
  if (items.length === 0) return <p className="text-sm text-stone-500 mt-4">No apiaries with public share links yet.</p>;

  return (
    <div className="mt-4 space-y-2" data-testid="featured-manager">
      <p className="text-xs text-stone-500 mb-3">
        <strong>Pinned</strong> apiaries always appear first (and skip the opt-in check). <strong>Hidden</strong> apiaries are excluded entirely from /api/public/featured.
      </p>
      {items.map((it) => (
        <div key={it.owner_id} className="flex items-center justify-between gap-3 p-3 border border-stone-200 rounded-lg" data-testid={`featured-row-${it.owner_id}`}>
          <div className="flex items-center gap-3 min-w-0">
            {it.avatar_url ? (
              <img src={it.avatar_url} alt="" className="w-9 h-9 rounded-full object-cover border border-stone-200" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-800 grid place-items-center text-sm font-semibold">{(it.name || "?")[0]?.toUpperCase()}</div>
            )}
            <div className="min-w-0">
              <div className="text-sm font-medium truncate">{it.name}</div>
              <div className="text-xs text-stone-500 truncate">
                {it.email} · {it.hives_count} hive{it.hives_count === 1 ? "" : "s"}
                {it.has_password && " · password-protected"}
                {!it.opted_in && " · NOT opted in"}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <Link to={`/share/${it.slug}`} target="_blank" className="text-stone-400 hover:text-stone-700"><ExternalLink className="w-4 h-4" /></Link>
            {it.override === "pin" ? (
              <Button size="sm" variant="default" onClick={() => clearOverride(it.owner_id)} className="bg-amber-500 hover:bg-amber-600 rounded-full" data-testid={`featured-unpin-${it.owner_id}`}>
                <Pin className="w-3.5 h-3.5 mr-1.5" /> Pinned
              </Button>
            ) : it.override === "hide" ? (
              <Button size="sm" variant="outline" onClick={() => clearOverride(it.owner_id)} className="rounded-full border-red-200 text-red-700 hover:bg-red-50" data-testid={`featured-unhide-${it.owner_id}`}>
                <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hidden
              </Button>
            ) : (
              <>
                <Button size="sm" variant="ghost" onClick={() => setOverride(it.owner_id, "pin")} className="rounded-full" data-testid={`featured-pin-${it.owner_id}`}>
                  <Pin className="w-3.5 h-3.5 mr-1.5" /> Pin
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setOverride(it.owner_id, "hide")} className="rounded-full text-stone-500 hover:text-red-700" data-testid={`featured-hide-${it.owner_id}`}>
                  <EyeOff className="w-3.5 h-3.5 mr-1.5" /> Hide
                </Button>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ───── Landing editor ─────
function LandingEditor() {
  const [c, setC] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    api.get("/public/site/landing").then((r) => setC(r.data));
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/admin/site/landing", c);
      toast.success("Landing page saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const updateFeature = (i, k, v) => {
    const features = [...c.features]; features[i] = { ...features[i], [k]: v };
    setC({ ...c, features });
  };

  const uploadHero = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const url = await uploadToCloudinary(f);
      setC({ ...c, hero_image_url: url });
      toast.success("Hero image uploaded");
    } catch (err) { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  if (!c) return <p className="text-sm text-stone-500 mt-4">Loading…</p>;

  return (
    <div className="mt-4 space-y-4" data-testid="landing-editor">
      <Row label="Hero eyebrow"><Input value={c.hero_eyebrow} onChange={(e) => setC({ ...c, hero_eyebrow: e.target.value })} data-testid="landing-hero-eyebrow-input" /></Row>
      <Row label="Hero title"><Input value={c.hero_title} onChange={(e) => setC({ ...c, hero_title: e.target.value })} data-testid="landing-hero-title-input" /></Row>
      <Row label="Hero subtitle"><Textarea rows={2} value={c.hero_subtitle} onChange={(e) => setC({ ...c, hero_subtitle: e.target.value })} data-testid="landing-hero-subtitle-input" /></Row>

      <div className="grid sm:grid-cols-2 gap-3">
        <Row label="Primary CTA label"><Input value={c.cta_primary_label} onChange={(e) => setC({ ...c, cta_primary_label: e.target.value })} /></Row>
        <Row label="Primary CTA link"><Input value={c.cta_primary_link} onChange={(e) => setC({ ...c, cta_primary_link: e.target.value })} /></Row>
        <Row label="Secondary CTA label"><Input value={c.cta_secondary_label} onChange={(e) => setC({ ...c, cta_secondary_label: e.target.value })} /></Row>
        <Row label="Secondary CTA link"><Input value={c.cta_secondary_link} onChange={(e) => setC({ ...c, cta_secondary_link: e.target.value })} /></Row>
      </div>

      <Row label="Hero image">
        <div className="flex items-center gap-3 flex-wrap">
          <img src={c.hero_image_url} alt="" className="w-28 h-28 rounded-lg object-cover border border-stone-200" />
          <div className="flex-1 min-w-[200px]">
            <Input value={c.hero_image_url} onChange={(e) => setC({ ...c, hero_image_url: e.target.value })} placeholder="https://…" data-testid="landing-hero-image-url" />
            <label className="text-xs text-amber-700 hover:underline mt-1 inline-flex items-center gap-1 cursor-pointer" data-testid="landing-hero-upload-label">
              <ImageIcon className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload new"}
              <input type="file" accept="image/*" onChange={uploadHero} className="hidden" />
            </label>
          </div>
        </div>
      </Row>

      <div>
        <Label className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Feature bullets (3)</Label>
        <div className="space-y-2 mt-2">
          {c.features.map((f, i) => (
            <div key={i} className="grid grid-cols-[120px_1fr_2fr] gap-2 items-center" data-testid={`landing-feature-${i}`}>
              <Select value={f.icon} onValueChange={(v) => updateFeature(i, "icon", v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{ICON_CHOICES.map((o) => <SelectItem key={o.v} value={o.v}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input value={f.label} onChange={(e) => updateFeature(i, "label", e.target.value)} placeholder="Label" />
              <Input value={f.description || ""} onChange={(e) => updateFeature(i, "description", e.target.value)} placeholder="Description (optional)" />
            </div>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <FieldGroup title="Inspection card">
          <Input value={c.inspection_card.title} onChange={(e) => setC({ ...c, inspection_card: { ...c.inspection_card, title: e.target.value } })} placeholder="Title" />
          <Input value={c.inspection_card.subtitle} onChange={(e) => setC({ ...c, inspection_card: { ...c.inspection_card, subtitle: e.target.value } })} placeholder="Subtitle" />
          <Input value={c.inspection_card.body} onChange={(e) => setC({ ...c, inspection_card: { ...c.inspection_card, body: e.target.value } })} placeholder="Body" />
        </FieldGroup>
        <FieldGroup title="Upcoming card">
          <Input value={c.upcoming_card.title} onChange={(e) => setC({ ...c, upcoming_card: { ...c.upcoming_card, title: e.target.value } })} placeholder="Title" />
          <Input value={c.upcoming_card.subtitle} onChange={(e) => setC({ ...c, upcoming_card: { ...c.upcoming_card, subtitle: e.target.value } })} placeholder="Subtitle" />
          <Input value={c.upcoming_card.body} onChange={(e) => setC({ ...c, upcoming_card: { ...c.upcoming_card, body: e.target.value } })} placeholder="Body" />
        </FieldGroup>
      </div>

      <Row label="Footer text"><Input value={c.footer_text} onChange={(e) => setC({ ...c, footer_text: e.target.value })} data-testid="landing-footer-input" /></Row>

      <div className="flex items-center justify-between gap-3 pt-3 border-t border-stone-200">
        <a href="/" target="_blank" rel="noreferrer" className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> Preview</a>
        <Button onClick={save} disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="landing-save-button">
          {saving ? "Saving…" : "Save landing page"}
        </Button>
      </div>
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}

function FieldGroup({ title, children }) {
  return (
    <div className="rounded-lg border border-stone-200 p-3 space-y-2">
      <div className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">{title}</div>
      {children}
    </div>
  );
}

// ───── Pages manager ─────
function PagesManager() {
  const [pages, setPages] = useState(null);
  const [editing, setEditing] = useState(null); // page object
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newSlug, setNewSlug] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/pages");
    setPages(data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newTitle || !newSlug) return;
    try {
      const { data } = await api.post("/admin/pages", { title: newTitle, slug: newSlug, blocks: [] });
      toast.success("Page created");
      setCreatingOpen(false); setNewTitle(""); setNewSlug("");
      await load();
      setEditing(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete page "${p.title}"?`)) return;
    try {
      await api.delete(`/admin/pages/${p.id}`);
      toast.success("Deleted");
      if (editing?.id === p.id) setEditing(null);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const togglePublish = async (p) => {
    try {
      const { data } = await api.patch(`/admin/pages/${p.id}`, { published: !p.published });
      toast.success(data.published ? "Published" : "Unpublished");
      await load();
      if (editing?.id === p.id) setEditing(data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const slugify = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  if (editing) return <PageEditor page={editing} onClose={() => { setEditing(null); load(); }} />;

  return (
    <div className="mt-4 space-y-3" data-testid="pages-manager">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-stone-500">Custom pages live at <code className="bg-stone-100 px-1.5 py-0.5 rounded">/p/&lt;slug&gt;</code> when published.</p>
        <Dialog open={creatingOpen} onOpenChange={setCreatingOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="pages-create-open"><Plus className="w-4 h-4 mr-1.5" /> New page</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-display text-2xl font-medium">New page</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div><Label>Title</Label><Input value={newTitle} onChange={(e) => { setNewTitle(e.target.value); if (!newSlug) setNewSlug(slugify(e.target.value)); }} data-testid="pages-create-title" /></div>
              <div>
                <Label>Slug</Label>
                <div className="flex items-center mt-1.5">
                  <span className="text-xs text-stone-400 mr-1">/p/</span>
                  <Input value={newSlug} onChange={(e) => setNewSlug(slugify(e.target.value))} className="font-mono" data-testid="pages-create-slug" />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button onClick={create} disabled={!newTitle || !newSlug} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="pages-create-submit">Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {pages === null ? <p className="text-sm text-stone-500">Loading…</p> : pages.length === 0 ? (
        <p className="text-sm text-stone-500 py-6 text-center">No pages yet. Create your first one above.</p>
      ) : (
        <ul className="space-y-2">
          {pages.map((p) => (
            <li key={p.id} className="flex items-center justify-between gap-3 p-3 border border-stone-200 rounded-lg" data-testid={`page-row-${p.id}`}>
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">{p.title}</div>
                <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap">
                  <code className="bg-stone-100 px-1 rounded">/p/{p.slug}</code>
                  {p.published ? <span className="text-emerald-700">● Published</span> : <span className="text-stone-400">○ Draft</span>}
                  · {p.blocks.length} block{p.blocks.length === 1 ? "" : "s"}
                </div>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                {p.published && <Link to={`/p/${p.slug}`} target="_blank" className="text-stone-400 hover:text-stone-700"><ExternalLink className="w-4 h-4" /></Link>}
                <Button size="sm" variant="outline" onClick={() => setEditing(p)} className="rounded-full" data-testid={`page-edit-${p.id}`}>Edit</Button>
                <Button size="sm" variant="ghost" onClick={() => togglePublish(p)} className="rounded-full" data-testid={`page-publish-${p.id}`}>
                  {p.published ? "Unpublish" : "Publish"}
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(p)} className="text-stone-400 hover:text-red-700 h-8 w-8" data-testid={`page-delete-${p.id}`}>
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ───── Single page editor ─────
const BLOCK_TYPES = [
  { v: "hero", label: "Hero" },
  { v: "text", label: "Text" },
  { v: "image", label: "Image" },
  { v: "two_column", label: "Two columns" },
  { v: "cta", label: "Call to action" },
  { v: "faq", label: "FAQ" },
];

function PageEditor({ page, onClose }) {
  const [p, setP] = useState(page);
  const [saving, setSaving] = useState(false);

  const update = (patch) => setP({ ...p, ...patch });
  const setBlock = (id, data) => setP({ ...p, blocks: p.blocks.map((b) => b.id === id ? { ...b, data } : b) });
  const moveBlock = (id, dir) => {
    const i = p.blocks.findIndex((b) => b.id === id);
    if (i < 0) return;
    const j = i + dir;
    if (j < 0 || j >= p.blocks.length) return;
    const blocks = [...p.blocks];
    [blocks[i], blocks[j]] = [blocks[j], blocks[i]];
    setP({ ...p, blocks });
  };
  const removeBlock = (id) => setP({ ...p, blocks: p.blocks.filter((b) => b.id !== id) });
  const addBlock = (type) => {
    const data = defaultData(type);
    setP({ ...p, blocks: [...p.blocks, { id: crypto.randomUUID(), type, data }] });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/admin/pages/${p.id}`, { title: p.title, slug: p.slug, blocks: p.blocks });
      toast.success("Saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <div className="mt-4 space-y-4" data-testid="page-editor">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <Button variant="ghost" onClick={onClose} className="rounded-full" data-testid="page-editor-close"><X className="w-4 h-4 mr-1.5" /> Back to list</Button>
        <div className="flex items-center gap-2">
          {p.published && <Link to={`/p/${p.slug}`} target="_blank" className="text-sm text-amber-700 hover:underline inline-flex items-center gap-1"><ExternalLink className="w-3.5 h-3.5" /> View live</Link>}
          <Button onClick={save} disabled={saving} className="rounded-full bg-yellow-400 text-amber-900 hover:bg-yellow-300" data-testid="page-editor-save">{saving ? "Saving…" : "Save"}</Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <Row label="Title"><Input value={p.title} onChange={(e) => update({ title: e.target.value })} data-testid="page-editor-title" /></Row>
        <Row label="Slug"><Input value={p.slug} onChange={(e) => update({ slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-") })} className="font-mono" data-testid="page-editor-slug" /></Row>
      </div>

      <div className="space-y-3" data-testid="page-editor-blocks">
        {p.blocks.length === 0 && <p className="text-sm text-stone-500 text-center py-6">No blocks yet. Add your first one below.</p>}
        {p.blocks.map((b, idx) => (
          <div key={b.id} className="border border-stone-200 rounded-xl p-4" data-testid={`page-block-${b.id}`}>
            <div className="flex items-center justify-between gap-2 mb-3">
              <span className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold">Block {idx + 1} · {b.type.replace("_", " ")}</span>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="ghost" onClick={() => moveBlock(b.id, -1)} disabled={idx === 0} className="h-7 w-7" data-testid={`page-block-up-${b.id}`}><ArrowUp className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => moveBlock(b.id, 1)} disabled={idx === p.blocks.length - 1} className="h-7 w-7" data-testid={`page-block-down-${b.id}`}><ArrowDown className="w-3.5 h-3.5" /></Button>
                <Button size="icon" variant="ghost" onClick={() => removeBlock(b.id)} className="h-7 w-7 text-stone-400 hover:text-red-700" data-testid={`page-block-delete-${b.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            </div>
            <BlockForm type={b.type} data={b.data} onChange={(d) => setBlock(b.id, d)} />
          </div>
        ))}
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-stone-200">
        <span className="text-xs uppercase tracking-[0.15em] text-stone-500 font-semibold mr-2 self-center">Add block:</span>
        {BLOCK_TYPES.map((bt) => (
          <Button key={bt.v} size="sm" variant="outline" onClick={() => addBlock(bt.v)} className="rounded-full border-stone-300" data-testid={`add-block-${bt.v}`}>
            <Plus className="w-3 h-3 mr-1" /> {bt.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function defaultData(type) {
  switch (type) {
    case "hero": return { eyebrow: "", title: "Heading", subtitle: "" };
    case "text": return { body: "" };
    case "image": return { url: "", alt: "", caption: "" };
    case "two_column": return { left: "", right: "" };
    case "cta": return { title: "Ready to start?", subtitle: "", button_label: "Get started", button_link: "/register" };
    case "faq": return { title: "FAQ", items: [{ q: "", a: "" }] };
    default: return {};
  }
}

function BlockForm({ type, data, onChange }) {
  const set = (k, v) => onChange({ ...data, [k]: v });
  if (type === "hero") return (
    <div className="space-y-2">
      <Input value={data.eyebrow || ""} onChange={(e) => set("eyebrow", e.target.value)} placeholder="Eyebrow (optional)" />
      <Input value={data.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Title" />
      <Textarea rows={2} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle" />
    </div>
  );
  if (type === "text") return <Textarea rows={6} value={data.body || ""} onChange={(e) => set("body", e.target.value)} placeholder="Write…" />;
  if (type === "image") return (
    <div className="space-y-2">
      <ImagePicker url={data.url || ""} onChange={(u) => set("url", u)} />
      <Input value={data.alt || ""} onChange={(e) => set("alt", e.target.value)} placeholder="Alt text" />
      <Input value={data.caption || ""} onChange={(e) => set("caption", e.target.value)} placeholder="Caption (optional)" />
    </div>
  );
  if (type === "two_column") return (
    <div className="grid sm:grid-cols-2 gap-3">
      <Textarea rows={4} value={data.left || ""} onChange={(e) => set("left", e.target.value)} placeholder="Left column" />
      <Textarea rows={4} value={data.right || ""} onChange={(e) => set("right", e.target.value)} placeholder="Right column" />
    </div>
  );
  if (type === "cta") return (
    <div className="space-y-2">
      <Input value={data.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Title" />
      <Textarea rows={2} value={data.subtitle || ""} onChange={(e) => set("subtitle", e.target.value)} placeholder="Subtitle" />
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={data.button_label || ""} onChange={(e) => set("button_label", e.target.value)} placeholder="Button label" />
        <Input value={data.button_link || ""} onChange={(e) => set("button_link", e.target.value)} placeholder="Button link" />
      </div>
    </div>
  );
  if (type === "faq") return (
    <div className="space-y-2">
      <Input value={data.title || ""} onChange={(e) => set("title", e.target.value)} placeholder="Section title" />
      {(data.items || []).map((it, i) => (
        <div key={i} className="grid grid-cols-[1fr_auto] gap-2 items-start border-l-2 border-amber-200 pl-3">
          <div className="space-y-1.5">
            <Input value={it.q} onChange={(e) => { const items = [...data.items]; items[i] = { ...items[i], q: e.target.value }; set("items", items); }} placeholder="Question" />
            <Textarea rows={2} value={it.a} onChange={(e) => { const items = [...data.items]; items[i] = { ...items[i], a: e.target.value }; set("items", items); }} placeholder="Answer" />
          </div>
          <Button size="icon" variant="ghost" onClick={() => set("items", data.items.filter((_, j) => j !== i))} className="h-7 w-7 text-stone-400 hover:text-red-700"><Trash2 className="w-3.5 h-3.5" /></Button>
        </div>
      ))}
      <Button size="sm" variant="outline" onClick={() => set("items", [...(data.items || []), { q: "", a: "" }])} className="rounded-full"><Plus className="w-3 h-3 mr-1" /> Add Q&A</Button>
    </div>
  );
  return null;
}

function ImagePicker({ url, onChange }) {
  const [uploading, setUploading] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(true);
    try {
      const u = await uploadToCloudinary(f); onChange(u); toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  return (
    <div className="flex items-center gap-3 flex-wrap">
      {url && <img src={url} alt="" className="w-20 h-20 rounded-lg object-cover border border-stone-200" />}
      <div className="flex-1 min-w-[180px] space-y-1.5">
        <Input value={url} onChange={(e) => onChange(e.target.value)} placeholder="Image URL" />
        <label className="text-xs text-amber-700 hover:underline inline-flex items-center gap-1 cursor-pointer">
          <ImageIcon className="w-3 h-3" /> {uploading ? "Uploading…" : "Upload"}
          <input type="file" accept="image/*" onChange={upload} className="hidden" />
        </label>
      </div>
    </div>
  );
}
